"use client";

import { Check } from "lucide-react";
import * as React from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/atoms/tooltip";
import { cn } from "@/lib/utils";
import { MCP_DESCRIPTION_TOOLTIP_DELAY_MS } from "./constants";

export type McpToolCardProps = {
  /**
   * Friendly, human-readable tool title (e.g. "Send email"). The consumer is
   * responsible for humanizing; the card renders this verbatim, so passing a
   * raw id shows the id.
   */
  name: string;
  /** Fully-qualified tool id (e.g. "Gmail.SendEmail"), surfaced in the tooltip. */
  rawName?: string;
  /** Optional tool version, shown muted next to the name (e.g. "1.2.4"). */
  version?: string;
  description?: string;
  selected?: boolean;
  destructive?: boolean;
  readOnly?: boolean;
  onToggle?: () => void;
} & Omit<React.ComponentProps<"button">, "onClick" | "onToggle" | "name">;

function McpToolCardImpl({
  name,
  rawName,
  version,
  description,
  selected = false,
  destructive = false,
  readOnly = false,
  onToggle,
  className,
  ...rest
}: McpToolCardProps) {
  const card = (
    <button
      {...rest}
      aria-pressed={selected}
      className={cn(
        "flex cursor-pointer items-start gap-2.5 rounded-lg border p-3 text-left transition-[background-color,border-color,box-shadow,transform] duration-[var(--duration-normal)] ease-[var(--ease-smooth)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 active:scale-[0.98] motion-reduce:transition-colors motion-reduce:active:scale-100",
        selected
          ? "border-foreground/45 bg-muted/20 shadow-[var(--shadow-card)]"
          : "border-border hover:border-foreground/40 hover:shadow-[var(--shadow-card-hover)]",
        className
      )}
      data-slot="mcp-tool-card"
      data-status={selected ? "selected" : "none"}
      onClick={onToggle}
      type="button"
    >
      <span
        aria-hidden
        className={cn(
          "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-[4px] border transition-colors",
          selected
            ? "border-primary bg-primary text-primary-foreground"
            : "border-input"
        )}
      >
        {selected ? <Check className="size-3" /> : null}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-start justify-between gap-3">
          <span className="flex min-w-0 items-baseline gap-1.5">
            <span className="truncate font-medium text-sm">{name}</span>
            {version ? (
              <span className="shrink-0 font-normal text-muted-foreground text-xs tabular-nums">
                v{version}
              </span>
            ) : null}
          </span>
          <span className="flex shrink-0 items-center gap-1.5 self-start">
            {destructive ? (
              <span className="ds-focus-in rounded-full bg-destructive/10 px-1.5 py-0.5 font-medium text-[10px] text-destructive uppercase tracking-wide">
                Destructive
              </span>
            ) : null}
            {readOnly ? (
              <span className="ds-focus-in rounded-full bg-muted px-1.5 py-0.5 font-medium text-[10px] text-muted-foreground uppercase tracking-wide">
                Read-only
              </span>
            ) : null}
          </span>
        </span>
        {description ? (
          <span className="mt-0.5 line-clamp-1 text-muted-foreground text-xs leading-snug">
            {description}
          </span>
        ) : null}
      </span>
    </button>
  );

  // The card clamps its description to one line, so the full text lives in a
  // keyboard-focusable tooltip with the whole card as the trigger.
  if (!description) {
    return card;
  }

  return (
    <TooltipProvider delay={MCP_DESCRIPTION_TOOLTIP_DELAY_MS}>
      <Tooltip>
        <TooltipTrigger render={card} />
        <TooltipContent
          className="max-w-xs flex-col items-start gap-0.5 text-left text-xs"
          side="bottom"
        >
          {rawName && rawName !== name ? (
            <span className="font-medium">{rawName}</span>
          ) : null}
          <span>{description}</span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export const McpToolCard = React.memo(McpToolCardImpl);
